import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import Dialog from '@mui/material/Dialog'
import CheckIcon from '@mui/icons-material/Check'
import fetcher from '../../../fetcher'
import { usePersonDetailStore } from '../stores/personDetailStore'
import { QuoteCard } from './QuoteCard'
import { VehicleCardBody } from './VehicleCardBody'
import { vehicleToQuote } from './vehicleToQuote'
import { getTermMonths, soldVehicleParts, wonOppToQuote } from './wonOppToQuote'
import { useOptions, VehicleSelect } from './vehicleSelects'
import { toISODate, toMDY } from './crmDates'
import {
    BROKER_FEE_KEY, editableProfitRows, initialProfitRows, parseMoney, ProfitBreakdownField,
    rowsToItems, rowsToProfitPayload, TRADE_IN_KEY,
} from './ProfitBreakdown'
import { keepEscapeInOpenSelect } from '../selectEscape'
import './MarkSoldModal.scss'

// The rep's local calendar day; toISOString() would be tomorrow on a US evening.
const todayISO = () => {
    const d = new Date()
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
// `new Date(iso)` is UTC midnight, so the month math stays in UTC too.
const addMonthsISO = (iso, months) => {
    if (!iso) return ''
    const d = new Date(iso)
    d.setUTCMonth(d.getUTCMonth() + (Number(months) || 0))
    return d.toISOString().slice(0, 10)
}

// Whole dollars, as the rail card shows it.
const profitSeed = (q) => (q?.profit ? String(Math.round(Number(q.profit))) : '')

const DEAL_TYPES = [['LEASE', 'Lease'], ['FINANCE', 'Finance'], ['CASH', 'Cash']]

const yearOptions = () => {
    const y = new Date().getFullYear()
    return [String(y + 1), String(y), String(y - 1)]
}

// A Current Vehicle as it reads on its card: name, then trim.
const tradeInLabel = (quote) => [quote.description, quote.spec].filter(Boolean).join(' ')

const VEHICLE_PARTS = ['year', 'make', 'model', 'trim']

const hasSnapshot = (opportunity) => Object.keys(opportunity?.trade_in_vehicle_details || {}).length > 0

// A saved deal's trade-in as the form holds it: the `current` kind means "as
// recorded", so an untouched one is never sent. A purchase names its car in
// sold_vehicle, a sale in trade_in_vehicle; a purchase from before snapshots
// still has its car, by name only.
export const savedTradeIn = (opportunity) => {
    const has = Boolean(opportunity?.trade_in_vehicle || hasSnapshot(opportunity)
        || (opportunity?.is_vehicle_purchase && opportunity.sold_vehicle))
    if (!has) return null
    const label = opportunity.is_vehicle_purchase ? opportunity.sold_vehicle : opportunity.trade_in_vehicle
    return { kind: 'current', id: null, label: label || 'Vehicle' }
}

// `vehicles` and `soldVehicles` are Current Vehicles' own two lists, so every
// car it shows can be traded in. `mode="edit"` reopens a completed sale
// (Edit Deal): seeded from what it recorded, saved by edit_sale, deletable
// through `onDelete`; a purchase's recorded car opens in `onVehicleDetails`.
// Its Salesperson select lists `salespeople`, changeable only with `canReassign`.
// Close mode is untouched by it.
export const MarkSoldModal = ({
    open, onClose, opportunity, vehicles = [], soldVehicles = [], mode = 'close', onDelete, onVehicleDetails,
    salespeople = [], canReassign = false,
}) => {
    const isEdit = mode === 'edit'
    // A purchase sold no car: it has a date, profit, dealership and its car.
    const isPurchase = isEdit && Boolean(opportunity?.is_vehicle_purchase)
    const markSold = usePersonDetailStore((s) => s.markSold)
    const editSale = usePersonDetailStore((s) => s.editSale)
    const person = usePersonDetailStore((s) => s.person)
    const fetchPerson = usePersonDetailStore((s) => s.fetchPerson)
    const quote = opportunity?.selected_deal_detail || opportunity?.quotes_detail?.[0]
    const quotes = opportunity?.quotes_detail || []
    // Edit mode: what the form opened with, so only changes are sent.
    const initial = useRef(null)
    // Edit mode reseeds the car from a quote only once the rep picks one.
    const quotePicked = useRef(false)

    const [f, setF] = useState({})
    const [saving, setSaving] = useState(false)
    // Which quote the sale is for — local until submit, sent as selected_deal.
    const [selectedQuoteId, setSelectedQuoteId] = useState(null)
    // The profit breakdown: Trade-in, Broker Fee, then the rep's own rows. Local
    // to the modal like every other field here, so nothing is written until the
    // deal closes.
    const [profitRows, setProfitRows] = useState(() => initialProfitRows())
    // A different card reseeds the grid from its quote. Its checkbox fires on
    // the selected card too, and that click must not wipe what the rep typed.
    const selectQuote = (q) => {
        if (q.id === selectedQuoteId) return
        setSelectedQuoteId(q.id)
        // A saved deal's profit is money already recorded, not a quote's guess.
        if (isEdit) quotePicked.current = true
        else {
            setProfitRows(initialProfitRows(profitSeed(q)))
            // The old quote's VIN never outlives the pick, even if this one fails to load.
            set({ vin: '' })
        }
    }

    // The rail shows quotes or trade-ins; neither selection depends on which.
    // A trade-in is {kind, id} (adapted card ids are null or a Deal's), plus
    // its label, so the summary survives a refetch blanking the lists.
    const [railMode, setRailMode] = useState('quotes')
    const [tradeIn, setTradeIn] = useState(null)
    // A purchase must keep a car, so its pick can be swapped but not cleared.
    const toggleTradeIn = (kind, id, label) => setTradeIn((t) => (
        t?.kind === kind && t.id === id ? (isPurchase ? t : null) : { kind, id, label }
    ))
    const tradeInCards = [
        ...vehicles.map((v) => ({ key: `v${v.id}`, kind: 'vehicle', id: v.id, quote: vehicleToQuote(v) })),
        // The sale being edited can't trade in its own car.
        ...soldVehicles.filter((o) => !isEdit || o.id !== opportunity?.id)
            .map((o) => ({ key: `o${o.id}`, kind: 'opportunity', id: o.id, quote: wonOppToQuote(o) })),
    ]

    useEffect(() => {
        if (!open) return
        if (isEdit) {
            const seeded = {
                ...soldVehicleParts(opportunity),
                inception: toISODate(opportunity.inception_date),
                term: String(getTermMonths(opportunity.inception_date, opportunity.maturity_date) ?? ''),
                expiration: toISODate(opportunity.maturity_date),
                dealType: opportunity.deal_type || 'LEASE',
                dealership: opportunity.dealership || '',
                salesperson: String(opportunity.owner_detail?.id ?? ''),
            }
            const rows = editableProfitRows(opportunity)
            const trade = savedTradeIn(opportunity)
            initial.current = { f: seeded, rows, tradeIn: trade, quoteId: opportunity.selected_deal ?? null }
            quotePicked.current = false
            setSelectedQuoteId(opportunity.selected_deal ?? null)
            // A purchase's car is its trade-in, so the rail opens on those.
            setRailMode(isPurchase ? 'tradeIn' : 'quotes')
            setTradeIn(trade)
            setF(seeded)
            setProfitRows(rows)
            return
        }
        setSelectedQuoteId(quote?.id ?? null)
        setRailMode('quotes')
        setTradeIn(null)
        const inception = todayISO()
        const term = quote?.term || '36'
        setF({
            year: '', make: '', model: '', trim: '', vin: '',
            inception, term, expiration: addMonthsISO(inception, term),
            dealType: 'LEASE', dealership: '',
        })
        // The worksheet's profit has always been submitted as the broker fee, so
        // it keeps seeding that row. Trade-in starts empty: the deal's trade
        // *value*, payoff and equity are different numbers from the profit made
        // on a trade, and guessing between them would invent money.
        setProfitRows(initialProfitRows(profitSeed(quote)))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, opportunity?.id])

    // The selected quote decides the vehicle and seeds the deal type. Quote
    // descriptions are not reliably parseable (they are often built from
    // base_car.name and carry no make), so read the structured car — and the
    // deal's own lease/purchase structure — off the deal itself.
    const carSeq = useRef(0)
    // A rep may overrule the seeded type; the sale record is allowed to differ
    // from how the worksheet was desked. Ref, not state: it must not re-render,
    // and the in-flight response below has to read its latest value.
    const dealTypeTouched = useRef(false)
    // Same for a VIN typed while the quote loads.
    const vinTouched = useRef(false)
    useEffect(() => {
        if (!open || selectedQuoteId == null) return
        // A saved deal keeps what it recorded until the rep picks a quote.
        if (isEdit && !quotePicked.current) return
        // An override belongs to the worksheet it was made on — a new pick reseeds.
        dealTypeTouched.current = false
        vinTouched.current = false
        const mySeq = ++carSeq.current
        fetcher.getDeal(selectedQuoteId)
            .then((res) => {
                if (mySeq !== carSeq.current) return
                const deal = res?.data || {}
                const car = deal.car_detail || {}
                const vin = String(deal.vin || '').trim().toUpperCase()
                setF((prev) => ({
                    ...prev,
                    year: car.year ? String(car.year) : '',
                    make: car.make || '',
                    model: car.model || '',
                    trim: car.trim || '',
                    // Same mapping as AttachQuotesModal and the backend's
                    // QuoteSummarySerializer: a one-pay purchase is cash, a
                    // one-pay lease is still a lease. A choice the rep made
                    // while this was in flight wins over the seed.
                    ...(dealTypeTouched.current ? {} : {
                        dealType: deal.is_purchase && deal.is_one_pay ? 'CASH'
                            : deal.is_purchase ? 'FINANCE' : 'LEASE',
                    }),
                    // Opportunity.vin holds 17; a longer one is left out.
                    ...(vinTouched.current ? {} : { vin: vin.length <= 17 ? vin : '' }),
                }))
            })
            .catch(() => { /* leave the selects empty — the user can still pick */ })
    }, [open, selectedQuoteId, isEdit])

    // Option lists, keyed on the values themselves so they cover both a user's
    // pick and a quote's auto-fill.
    const makes = useOptions(
        open && Boolean(f.year),
        () => fetcher.makesForYear(false),
        [open, f.year])
    const models = useOptions(
        open && Boolean(f.year && f.make),
        () => fetcher.modelsForMake(f.year, f.make, false),
        [open, f.year, f.make])
    const trims = useOptions(
        open && Boolean(f.year && f.make && f.model),
        () => fetcher.trimsForModel(f.year, f.make, f.model, false),
        [open, f.year, f.make, f.model])

    const set = (patch) => setF((prev) => ({ ...prev, ...patch }))

    // Uppercasing a typed VIN drops the caret at the end; put it back after
    // render, as AddCarModal does (uppercasing never changes the length).
    const caretRef = useRef(null)
    useLayoutEffect(() => {
        const held = caretRef.current
        if (!held) return
        caretRef.current = null
        held.input.setSelectionRange(held.start, held.end)
    })
    const onVinChange = (e) => {
        const { value, selectionStart, selectionEnd } = e.target
        caretRef.current = { input: e.target, start: selectionStart, end: selectionEnd }
        vinTouched.current = true
        set({ vin: value.toUpperCase() })
    }

    // Keep expiration in sync when inception or term changes.
    const syncExp = (patch) => {
        const next = { ...f, ...patch }
        // A saved deal may have no term on record; without one, its expiration
        // is left as recorded rather than collapsing onto the inception date.
        if (isEdit && !(Number(next.term) > 0)) set(patch)
        else set({ ...patch, expiration: addMonthsISO(next.inception, next.term) })
    }

    // The two fixed rows land in their own Opportunity columns; the rest go up as
    // other_profit_items, whose sum the backend keeps in other_profit. dealer_fee
    // and payer are deliberately absent — their inputs are gone, and sending a
    // zero would wipe what history already holds.
    const profitPayload = () => {
        const row = (key) => profitRows.find((r) => r.key === key)
        return {
            trade_in_profit: parseMoney(row(TRADE_IN_KEY).amount),
            trade_in_profit_status: row(TRADE_IN_KEY).status,
            broker_fee_profit: parseMoney(row(BROKER_FEE_KEY).amount),
            broker_fee_profit_status: row(BROKER_FEE_KEY).status,
            other_profit_items: rowsToItems(profitRows),
        }
    }

    const submit = async () => {
        setSaving(true)
        try {
            await markSold(opportunity.id, {
                ...(selectedQuoteId != null && { selected_deal: selectedQuoteId }),
                sold_vehicle: [f.year, f.make, f.model, f.trim].filter(Boolean).join(' '),
                // The parts too, so Edit Deal can reopen them; the server
                // rebuilds sold_vehicle from these.
                sold_vehicle_details: { year: f.year || '', make: f.make || '', model: f.model || '', trim: f.trim || '' },
                vin: (f.vin || '').trim(),
                inception_date: toMDY(f.inception),
                maturity_date: toMDY(f.expiration),
                deal_type: f.dealType,
                // No monthly_payment: its input is gone, and an empty one would
                // wipe a saved value.
                dealership: f.dealership,
                ...profitPayload(),
                ...(tradeIn && { trade_in: { kind: tradeIn.kind, id: tradeIn.id } }),
            })
            onClose()
        } catch (e) {
            /* toast handled in store */
        } finally {
            setSaving(false)
        }
    }

    // Only what changed, so a legacy value the form can't show (an unsplit
    // sold_vehicle, a free-text payment) is never overwritten by a blank.
    const editPayload = () => {
        const start = initial.current
        const payload = {}
        const changed = (key) => (f[key] || '') !== (start.f[key] || '')
        if (!isPurchase) {
            if (selectedQuoteId !== start.quoteId) payload.selected_deal = selectedQuoteId
            if (VEHICLE_PARTS.some(changed)) {
                payload.sold_vehicle_details = Object.fromEntries(VEHICLE_PARTS.map((k) => [k, f[k] || '']))
            }
            if (changed('dealType')) payload.deal_type = f.dealType
            if (changed('expiration')) payload.maturity_date = f.expiration ? toMDY(f.expiration) : null
        }
        if (changed('inception')) payload.inception_date = f.inception ? toMDY(f.inception) : null
        if (changed('dealership')) payload.dealership = f.dealership || ''
        if (changed('salesperson')) payload.user = Number(f.salesperson)
        if (profitRows !== start.rows) Object.assign(payload, rowsToProfitPayload(profitRows))
        if (tradeIn?.kind !== 'current') {
            if (tradeIn) payload.trade_in = { kind: tradeIn.kind, id: tradeIn.id }
            else if (start.tradeIn) payload.trade_in = null
        }
        return payload
    }

    const saveEdit = async () => {
        const payload = editPayload()
        if (!Object.keys(payload).length) { onClose(); return }
        setSaving(true)
        try {
            await editSale(opportunity.id, payload)
            onClose()
        } catch (e) {
            /* the interceptor toasts it; the form keeps what was typed */
        } finally {
            setSaving(false)
        }
    }

    // A recorded trade-in that is being removed or replaced, and whether its
    // car can go back to Current Vehicles. Said before saving, not after.
    const originalTradeIn = isEdit ? initial.current?.tradeIn : null
    // The recorded car's name follows the saved deal, so a details correction
    // made while this is open shows here too.
    const shownTradeIn = tradeIn?.kind === 'current'
        ? { ...tradeIn, label: savedTradeIn(opportunity)?.label || tradeIn.label }
        : tradeIn
    // Only the car as recorded, and only one with details to correct.
    const canOpenDetails = isPurchase && tradeIn?.kind === 'current' && hasSnapshot(opportunity) && Boolean(onVehicleDetails)
    const tradeInReplaced = Boolean(originalTradeIn) && tradeIn?.kind !== 'current'
    const restorable = Boolean(opportunity?.trade_in_restorable)
    const seededLabel = VEHICLE_PARTS.map((k) => initial.current?.f[k]).filter(Boolean).join(' ')
    const recordedAs = isEdit && !isPurchase && opportunity?.sold_vehicle
        && opportunity.sold_vehicle !== seededLabel ? opportunity.sold_vehicle : ''

    const name = person?.name || 'this contact'

    return (
        <Dialog open={open} onClose={onClose} maxWidth={false} PaperProps={{ className: 'sold-modal', onKeyDown: keepEscapeInOpenSelect }}>
            <div className="modal-head">
                <div className="modal-head-ic"><CheckIcon /></div>
                <div>
                    <div className="modal-title">{isEdit ? 'Edit the deal' : 'Close the deal'} — {name}</div>
                    {!isEdit && <div className="modal-sub">Record the car you sold. This moves them to Client status and starts the renewal clock from the inception date.</div>}
                </div>
                <button className="modal-x" onClick={onClose} aria-label="Close">✕</button>
            </div>

            <div className="modal-body">
                {/* ── Left: pick which quote closed ── */}
                <aside className="sold-rail">
                    <div className="sold-rail-title">{railMode === 'tradeIn' ? 'Select Trade In' : 'Select Vehicle'}</div>
                    {railMode === 'tradeIn' ? (
                        tradeInCards.length ? (
                            <div className="cdv sold-cdv sold-rail-cards">
                                <div className="quotes-grid">
                                    {tradeInCards.map((c) => {
                                        const toggle = () => toggleTradeIn(c.kind, c.id, tradeInLabel(c.quote))
                                        return (
                                            <QuoteCard
                                                key={c.key}
                                                quote={c.quote}
                                                vehicleBody={<VehicleCardBody quote={c.quote} />}
                                                showStatus={false}
                                                selectable
                                                selected={tradeIn?.kind === c.kind && tradeIn.id === c.id}
                                                onToggle={toggle}
                                                // Selects rather than opening the sold car's deal.
                                                onCardClick={toggle}
                                            />
                                        )
                                    })}
                                </div>
                            </div>
                        ) : (
                            <div className="sold-rail-empty">No current vehicles</div>
                        )
                    ) : quotes.length ? (
                        <div className="cdv sold-cdv sold-rail-cards">
                            <div className="quotes-grid">
                                {quotes.map((q) => (
                                    <QuoteCard
                                        key={q.id}
                                        quote={q}
                                        selectable
                                        selected={q.id === selectedQuoteId}
                                        onToggle={() => selectQuote(q)}
                                        // A quote repriced from the preview has a new
                                        // payment; refresh the person so the rail reads
                                        // the fresh quotes_detail. Not the saved deal
                                        // itself — that is the full DealSerializer, not
                                        // the summary shape these cards render.
                                        onSaved={() => person && fetchPerson(person.id)}
                                    />
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="sold-rail-empty">No quotes on this lead yet.</div>
                    )}
                    {railMode === 'quotes' && !isEdit && (
                        <div className="sold-search-row">
                            or <button type="button" className="sold-search-link" onClick={() => window.open('/quotes', '_blank')}>
                                search quotes
                            </button>
                        </div>
                    )}
                </aside>

                {/* ── Right: the existing form, unchanged ── */}
                <div className="sold-form">
                    {!isPurchase && <>
                    {!isEdit && (
                        <div className="mfield">
                            <div className="mfield-label">VIN</div>
                            <input aria-label="VIN" value={f.vin || ''} maxLength={17} placeholder="Enter VIN" onChange={onVinChange} />
                        </div>
                    )}
                    <div className="mfield-label">Vehicle</div>
                    <div className="mrow">
                        <VehicleSelect
                            label="Year" value={f.year} options={yearOptions()}
                            onChange={(year) => set({ year, make: '', model: '', trim: '' })}
                        />
                        <VehicleSelect
                            label="Make" value={f.make} options={makes} disabled={!f.year}
                            onChange={(make) => set({ make, model: '', trim: '' })}
                        />
                    </div>
                    <div className="mrow">
                        <VehicleSelect
                            label="Model" value={f.model} options={models} disabled={!f.make}
                            onChange={(model) => set({ model, trim: '' })}
                        />
                        <VehicleSelect
                            label="Trim" value={f.trim} options={trims} disabled={!f.model}
                            onChange={(trim) => set({ trim })}
                        />
                    </div>
                    </>}
                    {recordedAs && (
                        // A label the selects above can't show (imported, or
                        // typed over the quote's car before parts were kept).
                        <div className="sold-recorded-as">Recorded as {recordedAs}</div>
                    )}
                    {isPurchase ? (
                        <div className="mrow">
                            <div className="mfield"><div className="mfield-label">Purchase date</div><input type="date" value={f.inception || ''} onChange={(e) => set({ inception: e.target.value })} /></div>
                        </div>
                    ) : (
                    <div className="mrow">
                        <div className="mfield"><div className="mfield-label">Inception date</div><input type="date" value={f.inception || ''} onChange={(e) => syncExp({ inception: e.target.value })} /></div>
                        <div className="mfield"><div className="mfield-label">Term (months)</div><input value={f.term || ''} onChange={(e) => syncExp({ term: e.target.value })} /></div>
                        <div className="mfield"><div className="mfield-label">Expiration</div><input type="date" value={f.expiration || ''} onChange={(e) => set({ expiration: e.target.value })} /></div>
                    </div>
                    )}
                    <div className="mrow">
                        <ProfitBreakdownField rows={profitRows} onRowsChange={setProfitRows} saved={isEdit} />
                        {!isPurchase && (
                        <div className="mfield"><div className="mfield-label">Deal type</div>
                            <select value={f.dealType || 'LEASE'} onChange={(e) => { dealTypeTouched.current = true; set({ dealType: e.target.value }) }}>
                                {DEAL_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                            </select>
                        </div>
                        )}
                        <div className="mfield"><div className="mfield-label">Dealership</div><input value={f.dealership || ''} onChange={(e) => set({ dealership: e.target.value })} placeholder="e.g. BMW of Fort Lauderdale" /></div>
                    </div>
                    {isEdit && (
                        <div className="mrow">
                            <div className="mfield"><div className="mfield-label">Salesperson</div>
                                <select value={f.salesperson || ''} onChange={(e) => set({ salesperson: e.target.value })} disabled={!canReassign} aria-label="Salesperson">
                                    {salespeople.map((u) => <option key={u.id} value={String(u.id)}>{u.label}</option>)}
                                </select>
                            </div>
                        </div>
                    )}
                    {/* The trade-in stays named here whichever list the rail shows. */}
                    <div className="sold-trade-in">
                        <div className="mfield-label">Trade in</div>
                        {shownTradeIn && (
                            <div className="sold-trade-in-picked">
                                <span className="sold-trade-in-name" title={shownTradeIn.label}>{shownTradeIn.label || 'Vehicle'}</span>
                                {!isPurchase && (
                                    <button type="button" className="sold-trade-in-x" onClick={() => setTradeIn(null)} aria-label="Remove trade in">✕</button>
                                )}
                            </div>
                        )}
                        {canOpenDetails && (
                            <button type="button" className="sold-search-link" onClick={onVehicleDetails}>Vehicle details</button>
                        )}
                        {/* A purchase has no quotes to go back to. */}
                        {!isPurchase && (
                            <button type="button" className="sold-search-link" onClick={() => setRailMode((m) => (m === 'quotes' ? 'tradeIn' : 'quotes'))}>
                                {railMode === 'tradeIn' ? 'Back to quotes' : tradeIn ? 'Change trade in' : '+ Add trade in'}
                            </button>
                        )}
                        {tradeInReplaced && (
                            <div className={`sold-trade-in-note${restorable ? '' : ' is-warning'}`} role="status">
                                {restorable
                                    ? `${originalTradeIn.label} returns to Current Vehicles.`
                                    : `${originalTradeIn.label} can't be returned to Current Vehicles automatically.`}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="modal-foot">
                {isEdit && <button type="button" className="btn-delete-deal" onClick={onDelete} disabled={saving}>Delete deal</button>}
                <button className="btn-ghost" onClick={onClose}>Cancel</button>
                <button className="btn-sold" onClick={isEdit ? saveEdit : submit} disabled={saving || (isPurchase && !tradeIn)}>
                    <CheckIcon style={{ fontSize: 15 }} /> {saving ? 'Saving…' : isEdit ? 'Edit the deal' : 'Close deal & add car'}
                </button>
            </div>
        </Dialog>
    )
}
